import axios from "axios";
import React from "react";
import { useParams } from "react-router-dom";

import { classNames } from "../../helpers";
import { getUrlForBookings } from "../../utils/confighelpers";
import { clubSiteBase, lookVars, mergeLook, pickLang, useEmbedMode, useGoogleFont, wordsFor } from "../../utils/webWidgets";
import { useTrialClub } from "../trial/trialShared";

/**
 * Public webshop "Buy credits" (2026-11-01), on the club's address (or ?club=<link> on the main address):
 *   /shop                    products -> your details -> SMS code -> Stripe Checkout
 *   /shop/done/:productId    Stripe's success page
 *   /shop/cancelled          Stripe's cancel / back page
 * GET shopClub (club, look, products), POST shopRegister (code), POST shopCheckout (Stripe url) — functions/webshop.
 * Embedded on the club's website (?embed=1, public/embed.js): Stripe opens in a new tab (it can't run inside another
 * website). Preview (?preview=1, Website & webshop page): looks and steps come from the editor, buying is off.
 */

const API = () => getUrlForBookings();
const COUNTRIES = [
  ["+31", "NL"], ["+32", "BE"], ["+49", "DE"], ["+352", "LU"], ["+33", "FR"], ["+44", "UK"], ["+34", "ES"],
  ["+39", "IT"], ["+351", "PT"], ["+353", "IE"], ["+41", "CH"], ["+43", "AT"], ["+45", "DK"], ["+46", "SE"],
  ["+47", "NO"], ["+48", "PL"], ["+30", "GR"], ["+90", "TR"], ["+212", "MA"], ["+91", "IN"], ["+1", "US"],
];
const SAMPLE = [
  { _id: "sample1", productType: "classpass", productName: "5-class pass", price: 65, productFields: { noOfAllowedSessions: "5", validUntilConsumed: false, validityDays: 90 } },
  { _id: "sample2", productType: "classpass", productName: "10-class pass", price: 120, productFields: { noOfAllowedSessions: "10", validUntilConsumed: false, validityDays: 180 } },
  { _id: "sample3", productType: "subscription", productName: "Unlimited", price: 79, productFields: { unlimitedCredits: true, billingPeriod: { interval: "month", interval_count: 1 } } },
];

const apiMessage = (res, fallback) => (typeof res?.data?.message === "string" && res.data.message) || fallback;
async function call(method, path, body) {
  try {
    const res = method === "get" ? await axios.get(`${API()}${path}`, { params: body }) : await axios.post(`${API()}${path}`, body);
    if (res.status === 200 && res.data?.statusCode === 200) return { ok: true, data: res.data.data };
    return { ok: false, message: apiMessage(res, "") };
  } catch (e) {
    return { ok: false, message: apiMessage(e?.response, "") };
  }
}

const euro = (n, lang) => {
  const v = Number(n) || 0;
  const s = Number.isInteger(v) ? String(v) : v.toFixed(2);
  return `€${lang === "nl" ? s.replace(".", ",") : s}`;
};

/** Card facts for one product, in the visitor's language */
const facts = (p, w, lang, look) => {
  const f = p.productFields || {};
  if (p.productType === "subscription") {
    const unit = f.billingPeriod?.interval || "month";
    const n = parseInt(f.billingPeriod?.interval_count, 10) || 1;
    const per = n === 1 ? w.per[unit] || unit : `${n} ${w.per[unit] || unit}`;
    const renew = parseInt(f.creditsRenewalSessions, 10) || 0;
    return {
      price: euro(p.price, lang),
      suffix: `/${n === 1 ? w.perShort[unit] || unit : per}`,
      line1: f.unlimitedCredits === false && renew ? w.creditsEvery(renew, per) : w.unlimited,
      line2: w.paidAuto,
      sub: true,
    };
  }
  const credits = parseInt(f.noOfAllowedSessions, 10) || 0;
  const days = parseInt(f.validityDays, 10) || 0;
  return {
    price: euro(p.price, lang),
    suffix: "",
    line1: credits ? w.credits(credits) : "",
    line2: look.showValidity ? (f.validUntilConsumed !== false ? w.validUntilUsed : days ? w.validDays(days) : "") : "",
    perClass: look.showPerClass && credits > 1 ? w.perClass(euro(Math.round((p.price / credits) * 100) / 100, lang)) : "",
    sub: false,
  };
};

const Btn = ({ children, onClick, busy, disabled, type = "button", soft = false, className = "" }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={busy || disabled}
    className={classNames(
      "flex h-12 w-full items-center justify-center gap-2 rounded-[var(--fc-r-btn)] px-4 text-[15px] font-extrabold transition-opacity disabled:opacity-60",
      soft ? "border border-black/10 bg-white text-gray-900 hover:bg-gray-50" : "bg-[var(--fc-brand)] text-[color:var(--fc-on-brand)] hover:opacity-90",
      className
    )}
  >
    {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />}
    {children}
  </button>
);

const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-[13px] font-extrabold text-gray-800">
      {label}
      {hint && <span className="font-semibold text-gray-400"> · {hint}</span>}
    </span>
    {children}
  </label>
);
const inputCls = "h-12 w-full rounded-[var(--fc-r-field)] border border-black/15 bg-white px-3.5 text-[15px] text-gray-900 outline-none focus:border-[var(--fc-brand)] focus:ring-2 focus:ring-black/10";

function ProductCard({ p, w, lang, look, highlight, onBuy, disabled }) {
  const f = facts(p, w, lang, look);
  const label = look.labels?.shop || (f.sub ? w.subscribe : w.buy);
  return (
    <li className={classNames("relative flex flex-col rounded-[var(--fc-r-card)] bg-white p-5", highlight ? "border-2 border-[var(--fc-brand)]" : "border border-black/10")}>
      {highlight && (
        <span className="absolute -top-3 left-5 rounded-full bg-[var(--fc-brand)] px-2.5 py-0.5 text-xs font-extrabold text-[color:var(--fc-on-brand)]">{w.mostPopular}</span>
      )}
      <h3 className="text-[17px] font-black leading-snug text-gray-900">{p.productName}</h3>
      {(f.line1 || f.line2) && <p className="mt-1 text-sm text-gray-500">{[f.line1, f.line2].filter(Boolean).join(" · ")}</p>}
      <p className="mt-4 text-[32px] font-black leading-none tracking-tight text-gray-900">
        {f.price}
        {f.suffix && <span className="ml-1 text-base font-bold text-gray-500">{f.suffix}</span>}
      </p>
      {f.perClass && <p className="mt-1 text-xs text-gray-500">{f.perClass}</p>}
      {p.description && <p className="mt-3 line-clamp-3 text-[13px] text-gray-500">{p.description}</p>}
      <div className="mt-auto pt-5">
        <Btn onClick={() => onBuy(p)} disabled={disabled}>{label}</Btn>
      </div>
    </li>
  );
}

export default function ShopPage({ view = "shop" }) {
  const { productId: doneProductId } = useParams();
  const { link } = useTrialClub();
  const mode = useEmbedMode();
  const [data, setData] = React.useState(null); // { club, look, open, closedMessage, products }
  const [loadError, setLoadError] = React.useState("");
  const [step, setStep] = React.useState("products"); // products | details | code | pay | opened
  const [product, setProduct] = React.useState(null);
  const [form, setForm] = React.useState({ fullName: "", countryCode: "+31", phoneNumber: "", email: "" });
  const [token, setToken] = React.useState("");
  const [sentTo, setSentTo] = React.useState("");
  const [code, setCode] = React.useState("");
  const [testCode, setTestCode] = React.useState(""); // test environments send the code back (no SMS)
  const [payUrl, setPayUrl] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const topRef = React.useRef(null);

  React.useEffect(() => {
    if (!link) {
      setLoadError("This club wasn't found.");
      return;
    }
    (async () => {
      const r = await call("get", "shopClub", { club: link });
      if (r.ok) setData(r.data);
      else setLoadError(r.message || "This club wasn't found.");
    })();
  }, [link]);

  // preview: the editor sends the unsaved look and the step to show
  const pm = mode.previewMessage;
  const look = mergeLook(pm?.look || data?.look);
  const lang = pickLang(look, mode.lang);
  const w = wordsFor(lang);
  useGoogleFont(look.font === "site" ? mode.siteFont : look.font === "inter" ? "Inter" : look.font === "mulish" ? "Mulish" : "");
  const club = data?.club || {};
  const products = mode.preview && !(data?.products || []).length ? SAMPLE : data?.products || [];
  const shown = mode.preview && pm?.look
    ? [...products]
      .filter((p) => !(look.hiddenProducts || []).includes(String(p._id)))
      .sort((a, b) => {
        const order = look.productOrder || [];
        const ia = order.indexOf(String(a._id));
        const ib = order.indexOf(String(b._id));
        return (ia < 0 ? 1e6 : ia) - (ib < 0 ? 1e6 : ib);
      })
    : products;
  const previewStep = mode.preview ? pm?.step || "products" : null;
  const shownStep = previewStep === "details" ? "details" : previewStep === "done" ? "done" : previewStep === "products" ? "products" : step;
  const shownProduct = product || (previewStep ? shown.find((p) => String(p._id) === look.highlightProductId) || shown[0] : null);

  const scrollTop = () => {
    if (topRef.current && typeof topRef.current.scrollIntoView === "function" && !mode.embed) topRef.current.scrollIntoView({ block: "start" });
  };

  const start = (p) => {
    if (mode.preview) return;
    setProduct(p);
    setStep("details");
    setError("");
    scrollTop();
  };

  const register = async (e) => {
    e?.preventDefault?.();
    if (mode.preview || busy) return;
    setBusy(true);
    setError("");
    const r = await call("post", "shopRegister", { club: link, productId: product._id, ...form });
    setBusy(false);
    if (!r.ok) return setError(r.message || w.error);
    setToken(r.data.token);
    setSentTo(r.data.sentTo || "");
    setCode("");
    setTestCode(r.data.code || "");
    setStep("code");
  };

  const checkout = async (value) => {
    if (busy) return;
    setBusy(true);
    setError("");
    const r = await call("post", "shopCheckout", { token, code: value, origin: window.location.origin });
    setBusy(false);
    if (!r.ok) {
      setCode("");
      return setError(r.message || w.error);
    }
    setFirstName(r.data.firstName || "");
    if (!mode.embed) {
      window.location.assign(r.data.url); // own page: go to Stripe in this tab
      return;
    }
    setPayUrl(r.data.url); // inside the club's website: Stripe opens in a new tab (needs a click)
    setStep("pay");
  };

  const openPay = () => {
    window.open(payUrl, "_blank", "noopener");
    setStep("opened");
  };

  React.useEffect(() => {
    if (step === "code" && /^\d{4}$/.test(code) && token && !busy && !error) checkout(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const base = clubSiteBase(club.myBookingLink || link);
  const vars = lookVars(look, mode.siteFont);
  const wrap = (children) => (
    <div style={vars} className={classNames("min-h-0 w-full", mode.embed || mode.preview ? "bg-[var(--fc-bg)] p-1" : "min-h-screen bg-[#F7F4F8]")}>
      {!mode.embed && !mode.preview && (
        <header className="border-b border-black/5 bg-white">
          <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-4">
            {club.logo ? <img src={club.logo} alt="" className="h-10 w-10 rounded-xl object-cover" /> : <span className="h-10 w-10 rounded-xl bg-[var(--fc-brand)]" />}
            <span className="text-[17px] font-black text-gray-900">{club.name || ""}</span>
          </div>
        </header>
      )}
      <div ref={topRef} className={classNames("mx-auto w-full", mode.embed || mode.preview ? "max-w-5xl" : "max-w-5xl px-4 py-8")}>{children}</div>
    </div>
  );

  if (loadError) return wrap(<p className="rounded-[var(--fc-r-card)] bg-white p-6 text-center text-gray-600">{loadError}</p>);
  if (!data) return wrap(<div className="h-64 animate-pulse rounded-[var(--fc-r-card)] bg-black/5" aria-hidden="true" />);

  /* ---------- Stripe came back ---------- */
  if (view === "done" || shownStep === "done") {
    const bought = (data.products || []).find((p) => String(p._id) === String(doneProductId)) || (mode.preview ? shownProduct : null);
    return wrap(
      <div className="mx-auto max-w-md rounded-[var(--fc-r-card)] border border-black/10 bg-white p-7 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E8F7EE] text-[#1F6B3A]">
          <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12 5 5L20 7" /></svg>
        </span>
        <h1 className="mt-4 text-2xl font-black tracking-tight text-gray-900">{w.doneTitle(firstName)}</h1>
        <p className="mt-2 text-[15px] text-gray-600">{w.doneText(bought?.productName || "")}</p>
        <p className="mt-1 text-[13px] text-gray-500">{w.doneNew}</p>
        <div className="mt-6 flex flex-col gap-2">
          <a href={mode.preview ? undefined : `${base}members/schedule`} className="flex h-12 items-center justify-center rounded-[var(--fc-r-btn)] bg-[var(--fc-brand)] text-[15px] font-extrabold text-[color:var(--fc-on-brand)]">{w.bookFirst}</a>
          <a href={mode.preview ? undefined : `${base}get-app`} className="flex h-12 items-center justify-center rounded-[var(--fc-r-btn)] border border-black/10 bg-white text-[15px] font-extrabold text-gray-900">{w.getApp}</a>
          {club.website && (
            <a href={mode.preview ? undefined : club.website} className="mt-2 text-sm font-bold text-gray-600 underline">{w.backTo(club.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, ""))}</a>
          )}
        </div>
      </div>
    );
  }

  if (view === "cancelled") {
    return wrap(
      <div className="mx-auto max-w-md rounded-[var(--fc-r-card)] border border-black/10 bg-white p-7 text-center">
        <h1 className="text-2xl font-black tracking-tight text-gray-900">{w.cancelledTitle}</h1>
        <p className="mt-2 text-[15px] text-gray-600">{w.cancelledText}</p>
        <div className="mt-6 flex flex-col gap-2">
          <a href={`${base}shop`} className="flex h-12 items-center justify-center rounded-[var(--fc-r-btn)] bg-[var(--fc-brand)] text-[15px] font-extrabold text-[color:var(--fc-on-brand)]">{w.tryAgain}</a>
          {club.website && <a href={club.website} className="mt-2 text-sm font-bold text-gray-600 underline">{w.backTo(club.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, ""))}</a>}
        </div>
      </div>
    );
  }

  const powered = look.poweredBy && (
    <p className="mt-5 text-center text-xs text-gray-400">
      {w.poweredBy} <b className="text-gray-500">FitClub</b>
    </p>
  );

  /* ---------- products ---------- */
  if (shownStep === "products") {
    return wrap(
      <div>
        <h2 className="text-[22px] font-black tracking-tight text-gray-900">{w.buyCredits}</h2>
        <p className="mt-1 text-[15px] text-gray-600">{w.shopIntro}</p>
        {mode.preview && <p className="mt-2 text-xs font-bold text-gray-400">{w.previewOff}</p>}
        {!data.open && !mode.preview ? (
          <p className="mt-5 rounded-[var(--fc-r-card)] border border-black/10 bg-white p-6 text-center text-gray-600">{data.closedMessage || w.closed}</p>
        ) : shown.length === 0 ? (
          <p className="mt-5 rounded-[var(--fc-r-card)] border border-black/10 bg-white p-6 text-center text-gray-600">{w.noProducts}</p>
        ) : (
          <ul className={classNames("mt-6 grid grid-cols-1 gap-4", shown.length > 1 && "sm:grid-cols-2", shown.length > 2 && "md:grid-cols-3")}>
            {shown.map((p) => (
              <ProductCard key={p._id} p={p} w={w} lang={lang} look={look} highlight={String(p._id) === look.highlightProductId} onBuy={start} />
            ))}
          </ul>
        )}
        <p className="mt-5 text-center text-[13px] text-gray-500">{w.alreadyMember}</p>
        {powered}
      </div>
    );
  }

  /* ---------- buying steps ---------- */
  const f = shownProduct ? facts(shownProduct, w, lang, look) : null;
  const summary = shownProduct && (
    <div className="flex items-center justify-between gap-3 rounded-[var(--fc-r-field)] bg-black/[0.04] px-4 py-3">
      <span className="min-w-0">
        <span className="block truncate text-[15px] font-black text-gray-900">{shownProduct.productName}</span>
        {(f.line1 || f.line2) && <span className="block truncate text-xs text-gray-500">{[f.line1, f.line2].filter(Boolean).join(" · ")}</span>}
      </span>
      <span className="shrink-0 text-[17px] font-black text-gray-900">
        {f.price}
        {f.suffix && <span className="text-xs font-bold text-gray-500">{f.suffix}</span>}
      </span>
    </div>
  );
  const backBtn = (to) => (
    <button type="button" onClick={() => { setStep(to); setError(""); }} className="text-[13px] font-extrabold text-gray-500 hover:text-gray-800">
      ← {w.back}
    </button>
  );
  const errorBox = error && <p className="rounded-[var(--fc-r-field)] bg-[#FFE8EB] px-3.5 py-2.5 text-sm font-semibold text-[#B4232F]" role="alert">{error}</p>;

  return wrap(
    <div className="mx-auto flex max-w-md flex-col gap-4 rounded-[var(--fc-r-card)] border border-black/10 bg-white p-5 sm:p-6">
      {shownStep === "details" && (
        <form onSubmit={register} className="flex flex-col gap-4">
          <div className="flex items-center justify-between">{backBtn("products")}<span className="text-xs font-bold text-gray-400">{w.step(1, 2)}</span></div>
          {summary}
          <div>
            <h2 className="text-xl font-black text-gray-900">{w.yourDetails}</h2>
            <p className="text-sm text-gray-500">{w.detailsWhy}</p>
          </div>
          <Field label={w.fullName}>
            <input className={inputCls} autoComplete="name" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} required />
          </Field>
          <Field label={w.mobile}>
            <div className="flex gap-2">
              <select aria-label="Country code" className={classNames(inputCls.replace("w-full ", ""), "w-[104px] shrink-0 px-2")} value={form.countryCode} onChange={(e) => setForm({ ...form, countryCode: e.target.value })}>
                {COUNTRIES.map(([cc, iso]) => (
                  <option key={cc} value={cc}>{`${iso} ${cc}`}</option>
                ))}
              </select>
              <input className={classNames(inputCls.replace("w-full ", ""), "min-w-0 flex-1")} type="tel" inputMode="tel" autoComplete="tel-national" placeholder="6 12345678" value={form.phoneNumber} onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} required />
            </div>
          </Field>
          <Field label={w.email} hint={w.emailWhy}>
            <input className={inputCls} type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          </Field>
          <p className="text-xs leading-relaxed text-gray-500">{w.codeNote(club.name || "")}</p>
          {errorBox}
          <Btn type="submit" busy={busy}>{w.sendCode}</Btn>
          {mode.preview && <p className="text-center text-xs font-bold text-gray-400">{w.previewOff}</p>}
        </form>
      )}

      {shownStep === "code" && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">{backBtn("details")}<span className="text-xs font-bold text-gray-400">{w.step(2, 2)}</span></div>
          {summary}
          <div>
            <h2 className="text-xl font-black text-gray-900">{w.enterCode}</h2>
            <p className="text-sm text-gray-500">{w.sentTo(sentTo)}</p>
          </div>
          <input
            className={classNames(inputCls, "h-14 text-center text-2xl font-black tracking-[0.5em]")}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={4}
            aria-label={w.enterCode}
            value={code}
            onChange={(e) => {
              setError("");
              setCode(e.target.value.replace(/\D/g, "").slice(0, 4));
            }}
            autoFocus
          />
          {testCode && <p className="text-center text-xs font-bold text-gray-400">Test environment, code: {testCode}</p>}
          {errorBox}
          {busy && <p className="text-center text-sm font-semibold text-gray-500">…</p>}
          <button type="button" onClick={register} className="text-sm font-bold text-gray-600 underline">{w.resend}</button>
        </div>
      )}

      {(shownStep === "pay" || shownStep === "opened") && (
        <div className="flex flex-col gap-4">
          {summary}
          <div>
            <h2 className="text-xl font-black text-gray-900">{w.pay(f?.price || "")}</h2>
            <p className="text-sm text-gray-500">{w.payNote} {w.payNewTab}</p>
          </div>
          {shownStep === "opened" ? (
            <>
              <p className="rounded-[var(--fc-r-field)] bg-[#E8F7EE] px-3.5 py-3 text-sm font-semibold text-[#1F6B3A]">{w.payOpened}</p>
              <Btn soft onClick={openPay}>{w.payAgain} ↗</Btn>
            </>
          ) : (
            <Btn onClick={openPay}>{w.paySecurely} ↗</Btn>
          )}
        </div>
      )}
      {powered}
    </div>
  );
}
